import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Tag, Image, Tabs, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import CommonModal from 'PhishingManager/components/Modal';
import { TemplateDetails } from 'PhishingManager/pages/CampaignSteps';
import { useStores } from 'Hooks/useStore';
import styles from './LibraryModal.module.scss';

export interface LibraryModalProps {
  visible: boolean;
  loading: boolean;
  templateDetails: TemplateDetails | null;
  isPublished?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onLanguageChange: (language: number) => void;
}

const PUBLIC_PLACEHOLDER_IMG = process.env.PUBLIC_URL
  ? `${process.env.PUBLIC_URL}/placeholderImage.png`
  : '/placeholderImage.png';

export const LibraryModal: React.FC<LibraryModalProps> = ({
  visible,
  loading,
  templateDetails,
  isPublished = false,
  onClose,
  onEdit,
  onLanguageChange
}) => {
  const { t } = useTranslation();
  const { authStore } = useStores();
  const isSuperAdmin = Boolean(authStore?.isCurrentUserSuperAdmin);

  const [activeTab, setActiveTab] = useState('email');
  const [imgSrc, setImgSrc] = useState<string>(PUBLIC_PLACEHOLDER_IMG);
  const { TabPane } = Tabs;
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (templateDetails?.image_path) {
      setImgSrc(templateDetails.image_path);
    } else {
      setImgSrc(PUBLIC_PLACEHOLDER_IMG);
    }
  }, [templateDetails?.image_path, templateDetails?.id]);

  useEffect(() => {
    if (visible) {
      setActiveTab('email');

      const resetModalScroll = () => {
        const modalBody = document.querySelector('.phishing-preview-modal .ant-modal-body');
        const modalWrap = document.querySelector('.phishing-preview-modal .ant-modal-wrap');

        if (modalBody) modalBody.scrollTop = 0;
        if (modalWrap) modalWrap.scrollTop = 0;

        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
          let parent = modalContentRef.current.parentElement;
          while (parent) {
            parent.scrollTop = 0;
            parent = parent.parentElement;
          }
        }
      };

      resetModalScroll();
      const timer = setTimeout(resetModalScroll, 50);
      return () => clearTimeout(timer);
    }
  }, [visible, templateDetails?.id]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'IFRAME_EMAIL_CLICKED') {
        openInNewTab(templateDetails?.email_template_html ?? '');
      }
      if (event.data === 'IFRAME_LANDING_CLICKED') {
        openInNewTab(templateDetails?.landing_page_html ?? '');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [templateDetails]);

  const openInNewTab = (htmlContent: string) => {
    if (!htmlContent) return;
    const newWindow = window.open();
    newWindow?.document.write(htmlContent);
    newWindow?.document.close();
  };

  const categoryName =
    (templateDetails?.categories && templateDetails.categories.length > 0 && templateDetails.categories[0].name) ||
    (templateDetails as any)?.category ||
    '';

  const rawDifficulty = templateDetails?.difficulty || '';
  const formattedDifficulty = rawDifficulty
    ? rawDifficulty.charAt(0).toUpperCase() + rawDifficulty.slice(1).toLowerCase()
    : '';

  const difficultyLevel = useMemo(() => {
    const diff = rawDifficulty.toLowerCase().trim();
    if (diff === 'beginner') return 'beginner';
    if (diff === 'advanced') return 'advanced';
    return 'intermediate';
  }, [rawDifficulty]);

  const confirmBtnText = isSuperAdmin
    ? isPublished
      ? t('edit', 'Edit')
      : t('phishingTemplateManager.libraryModal.editAndPublish', 'Edit & Publish')
    : t('phishingManager.phishingModal.cancelText');

  const handleConfirmAction = isSuperAdmin ? onEdit : onClose;

  const targetTenantName =
    (templateDetails as Record<string, any>)?.target_tenant ||
    t('phishingTemplateManager.prefilledForm.allTenants');

  return (
    <CommonModal
      visible={visible}
      showConfirmBtn={true}
      confirmBtnDisabled={loading}
      confirmText={confirmBtnText}
      cancelText={isSuperAdmin ? t('phishingManager.phishingModal.cancelText', 'Close') : ''}
      iconType={null}
      onClose={onClose}
      onConfirm={handleConfirmAction}
      modalTitle={templateDetails?.name || t('phishingTemplateManager.libraryModal.defaultModalTitle')}
      parentClass="phishing-preview-modal"
    >
      <Spin spinning={loading}>
        <div ref={modalContentRef}>
          <div className={styles.previewHeaderCard}>
            <div className={styles.topSpacer} />

            <div className={styles.centerThumbnailWrapper}>
              <Image
                src={imgSrc}
                fallback={PUBLIC_PLACEHOLDER_IMG}
                onError={() => setImgSrc(PUBLIC_PLACEHOLDER_IMG)}
                alt={t('phishingManager.phishingModal.imageAlt')}
                preview={false}
                className={styles.centerThumbnail}
              />
            </div>

            {(categoryName || formattedDifficulty) && (
              <div className={styles.badgesWrapper}>
                {categoryName && (
                  <div className={styles.categoryBadge}>
                    <span className={styles.categoryBadgeText}>{categoryName}</span>
                  </div>
                )}
                {formattedDifficulty && (
                  <div className={`${styles.difficultyBadge} ${styles[difficultyLevel]}`}>
                    <span className={`${styles.difficultyBadgeText} ${styles[difficultyLevel]}`}>
                      {formattedDifficulty}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.descriptionSection}>
            <h3 className={styles.descriptionTitle}>
              {t('phishingManager.phishingModal.description.title', 'Description')}
            </h3>
            <p className={styles.descriptionText}>
              {templateDetails?.description || t('phishingManager.phishingModal.description.empty')}
            </p>
          </div>

          <div className="tabs-section">
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab={t('phishingManager.phishingModal.tabs.email')} key="email">
                <TemplatePreview
                  title={t('phishingManager.phishingModal.tabs.emailPreview')}
                  html={templateDetails?.email_template_html}
                  postMessageKey="IFRAME_EMAIL_CLICKED"
                />
              </TabPane>
              <TabPane tab={t('phishingManager.phishingModal.tabs.landingPage')} key="landing_page">
                <TemplatePreview
                  title={t('phishingManager.phishingModal.tabs.landingPagePreview')}
                  html={templateDetails?.landing_page_html}
                  postMessageKey="IFRAME_LANDING_CLICKED"
                />
              </TabPane>
            </Tabs>
          </div>

          <div className={styles.tenantSection}>
            <label className={styles.tenantLabel}>
              {t('phishingTemplateManager.libraryModal.tenantsLabel', 'Tenants')}
            </label>
            <div className={styles.tenantTag}>{targetTenantName}</div>
          </div>

          <div className={styles.languageSection}>
            <label className={styles.languageLabel}>
              {t('phishingTemplateManager.libraryModal.languagesLabel')}
            </label>
            <div className={styles.languageTags}>
              {templateDetails?.languages?.map((lang) => (
                <Tag
                  key={lang.id}
                  onClick={() => onLanguageChange(lang.id)}
                  className={`${styles.tagPointer} ${styles.languageTag}`}
                >
                  {lang.name}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      </Spin>
    </CommonModal>
  );
};

const TemplatePreview = ({
  title,
  html,
  postMessageKey
}: {
  title: string;
  html?: string;
  postMessageKey: string;
}) => {
  const injectIframeLogic = (content: string | undefined): string => {
    if (!content) return '';

    const logicScript = `
      <style>
        a { pointer-events: none !important; cursor: default !important; }
        html, body { cursor: pointer; margin: 0; padding: 0; }
      </style>
      <script>
        document.addEventListener('click', () => {
          window.parent.postMessage('${postMessageKey}', '*');
        });
      </script>
    `;

    if (content.includes('</head>')) {
      return content.replace('</head>', `${logicScript}</head>`);
    }

    if (content.includes('<body>')) {
      return content.replace('<body>', `<head>${logicScript}</head><body>`);
    }

    return `<head>${logicScript}</head>${content}`;
  };

  return (
    <div className="tab-content">
      <div>
        <iframe
          title={title}
          srcDoc={injectIframeLogic(html) || ''}
        />
      </div>
    </div>
  );
};

export default LibraryModal;